<script setup lang="ts">
import { SignedOut, useAuth, useClerk } from "@clerk/vue";
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useApi, fetchCSRFToken } from "./lib/api";
import AppSidebar from "@/components/AppSidebar.vue";
import CollapsibleSidebar from "@/components/CollapsibleSidebar.vue";
// ThemeToggle is currently unused on this page; removing to satisfy lints
import LucideIcon from "@/components/LucideIcon.vue";
import BillingTrialBanner from "@/components/billing/BillingTrialBanner.vue";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Toaster } from "@/components/ui/toast";
import { useAccount } from "@/composables/useAccount";
import { useAnalytics, trackWebVitals } from "@/composables/useAnalytics";
import { useBreakpoints } from "@/composables/useMediaQuery";
import { useTheme } from "@/composables/useTheme";

const { apiFetch } = useApi();
const { initTheme } = useTheme();
const { isMobile, isDesktop } = useBreakpoints();
const account = useAccount();
const router = useRouter();
const route = useRoute();
const isE2E = Boolean((import.meta as ImportMeta).env?.VITE_E2E);
const auth = isE2E ? null : useAuth();
const clerk = isE2E ? null : useClerk();
const isLoaded = isE2E ? ref(true) : auth!.isLoaded;
const userId = isE2E ? ref<string | null>(null) : auth!.userId;

// Session expiry dialog state
const showSessionExpired = ref(false);
const redirectAfterLogin = ref<string>("/");

function handleSessionExpired(e: Event) {
  try {
    const detail = (e as CustomEvent).detail as
      | { redirectPath?: string }
      | undefined;
    redirectAfterLogin.value =
      detail?.redirectPath || window.location.pathname + window.location.search;
  } catch {
    redirectAfterLogin.value =
      window.location.pathname + window.location.search;
  }
  showSessionExpired.value = true;
  // Prevent default redirection by the emitter; we'll handle UX here
  e.preventDefault?.();
}

function signInAgain() {
  const target = `/login?redirect=${encodeURIComponent(redirectAfterLogin.value)}`;
  window.open(target, "_self");
}

// Initialize analytics
useAnalytics();

onMounted(() => {
  initTheme();

  // Initialize web vitals tracking
  trackWebVitals();

  window.addEventListener(
    "session-expired",
    handleSessionExpired as unknown as (e: Event) => void,
  );
});

onBeforeUnmount(() => {
  window.removeEventListener(
    "session-expired",
    handleSessionExpired as unknown as (e: Event) => void,
  );
});

const accountState = account.state;
const accountError = account.error;

// Full-page routes that don't need the sidebar layout
// Story 5.4: Invitation and join flows are full-page
// Check both route.name and route.path to prevent sidebar flash on initial load
// (path is available immediately, name may take a tick to resolve)
const isFullPageRoute = computed(() => {
  const path = route.path;
  const name = route.name;
  return (
    name === "invite-accept" ||
    name === "join" ||
    name === "home" ||
    name === "login" ||
    name === "signup" ||
    name === "onboarding" ||
    path === "/" ||
    path === "/join" ||
    path === "/login" ||
    path === "/signup" ||
    path === "/onboarding" ||
    path.startsWith("/invite/accept")
  );
});

// Show routed page once account state is known, or for onboarding/full-page routes.
// The needsCompany redirect watch handles sending users to /onboarding.
const showRoutedPage = computed(() => {
  return (
    !!accountState.value || route.name === "onboarding" || isFullPageRoute.value
  );
});

const lastSyncedUserId = ref<string | null>(null);
const storedSidebar =
  typeof window !== "undefined"
    ? localStorage.getItem("sidebar-expanded")
    : null;
// Default to expanded for users who haven't set a preference yet
const sidebarExpanded = ref(
  storedSidebar === null ? true : storedSidebar === "true",
);
const mobileSidebarOpen = ref(false);

const needsCompany = computed(
  () => accountState.value?.state === "needs_company",
);

// Story 5.1: Redirect to onboarding wizard when user needs to create a company
// Story 5.4: Exclude invite/join flows from redirect - they handle company association
const onboardingExemptRoutes = ["onboarding", "invite-accept", "join"];
watch(
  needsCompany,
  (needs) => {
    if (needs && !onboardingExemptRoutes.includes(route.name as string)) {
      router.push({ name: "onboarding" });
    }
  },
  { immediate: true },
);

watch(sidebarExpanded, (newValue) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("sidebar-expanded", String(newValue));
  }
});

watch(
  () => [isLoaded.value, userId?.value ?? null] as const,
  async ([loaded, uid]) => {
    if (loaded && uid && uid !== lastSyncedUserId.value) {
      try {
        await apiFetch("/auth/sync", { method: "GET" });
        // Fetch CSRF token for cross-domain compatibility
        // This is needed because the frontend can't read cookies set by the backend domain
        // Pass the auth token since the endpoint requires authentication
        const tokenFn = auth?.getToken?.value;
        const authToken =
          typeof tokenFn === "function" ? await tokenFn() : null;
        await fetchCSRFToken(authToken ?? undefined);
      } catch {
        // no-op; backend will log if it fails
      }
      lastSyncedUserId.value = uid;
      await account.refresh();

      // Auto-activate Clerk organization if the user belongs to one but none is active.
      // This enables org:admin detection for dev tools (useOrganization().membership.role).
      // Only dev-team members (Jordan, Adam, Barb) have org memberships; regular users skip this.
      try {
        const c = clerk?.value;
        if (c && !c.organization) {
          // Prefer already-loaded memberships to avoid an extra API round-trip on every sign-in
          const loaded = c.user?.organizationMemberships;
          const orgId = loaded?.length ? loaded[0].organization.id : undefined;
          if (orgId) {
            await c.setActive({ organization: orgId });
          } else {
            // Fallback: fetch from API if local list is empty (e.g. first load)
            const fetched = await c.user?.getOrganizationMemberships();
            if (fetched?.data?.length) {
              await c.setActive({
                organization: fetched.data[0].organization.id,
              });
            }
          }
        }
      } catch (err) {
        // Best-effort; don't block app on org activation failure
        console.warn("Clerk org auto-activation failed", err);
      }
    }

    if (loaded && !uid) {
      lastSyncedUserId.value = null;
      // Clear account state and sessionStorage cache
      account.clearCache();
      accountError.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="min-h-screen bg-background text-foreground">
    <!-- Desktop Collapsible Sidebar (hidden for full-page routes) -->
    <CollapsibleSidebar
      v-if="!isE2E && isDesktop && !isFullPageRoute"
      class="print:hidden"
      :expanded="sidebarExpanded"
      @update:expanded="sidebarExpanded = $event"
    />

    <!-- Mobile Sheet Sidebar (hidden for full-page routes) -->
    <AppSidebar
      v-if="!isE2E && isMobile && !isFullPageRoute"
      class="print:hidden"
      :open="mobileSidebarOpen"
      @update:open="mobileSidebarOpen = $event"
    >
      <template #trigger>
        <Button
          variant="outline"
          size="icon"
          aria-label="Open menu"
          class="fixed top-3 left-3 z-40 md:hidden"
        >
          <LucideIcon name="menu" />
          <span class="sr-only">Open menu</span>
        </Button>
      </template>
    </AppSidebar>

    <div
      :class="[
        'transition-all duration-300 print:ml-0',
        !isE2E && isDesktop && !isFullPageRoute
          ? sidebarExpanded
            ? 'ml-52'
            : 'ml-16'
          : '',
      ]"
    >
      <!-- Session expired overlay -->
      <div
        v-if="showSessionExpired"
        class="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4"
      >
        <div
          class="w-full max-w-sm rounded-xl border bg-background p-5 shadow-lg"
        >
          <div class="mb-2 text-base font-semibold">Session expired</div>
          <p class="mb-4 text-sm text-muted-foreground">
            Your session has expired. Sign in again to continue where you left
            off.
          </p>
          <div class="flex gap-2 justify-end">
            <Button variant="outline" @click="showSessionExpired = false"
              >Dismiss</Button
            >
            <Button @click="signInAgain">Sign in again</Button>
          </div>
        </div>
      </div>

      <!-- Billing trial banner (Tech-Spec: Stripe Billing, Task 23) -->
      <BillingTrialBanner v-if="!isFullPageRoute && accountState" />

      <main class="flex-1">
        <RouterView v-if="showRoutedPage" />
        <template v-else-if="isE2E">
          <div>
            <section class="space-y-3">
              <Card>
                <CardHeader>
                  <CardTitle>Acme</CardTitle>
                  <CardDescription>
                    You're connected as jordan@pando.com. Use the actions above
                    to continue setup.
                  </CardDescription>
                </CardHeader>
              </Card>
            </section>
          </div>
        </template>
        <!-- Only show SignedOut fallback for non-full-page routes -->
        <!-- Full-page routes (home, login, signup, join, invite) handle their own unauthenticated UI -->
        <SignedOut v-if="!isFullPageRoute">
          <div
            class="mx-auto rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground max-w-xl md:max-w-2xl lg:max-w-3xl"
          >
            Sign in to create or join your company's workspace.
          </div>
        </SignedOut>
      </main>

      <!-- Global Toast Provider -->
      <Toaster />
    </div>
  </div>
</template>
